import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { LabMachineModel } from "src/types/models/labs-configuration";
import { handleBackendHttpError } from "../handleBackendHttpError";
import { buildResourcePathForCurrentUser } from "../ResourceBuilder";
import type { RootState } from "../store";

const tagTypes: string[] = ["AccountLabMachines"];

const labsConfigurationApi = createApi({
    reducerPath: "lobbie/account/labs-configuration",
    baseQuery: fetchBaseQuery({}),
    tagTypes,
    endpoints: (builder) => ({
        getLabMachinesForAccount: builder.query<LabMachineModel[], void>({
            queryFn: async (_, api) => {
                const fetchUrl = buildResourcePathForCurrentUser(
                    api.getState() as RootState,
                    "/labs/settings/machines",
                );
                try {
                    return await axios.get<LabMachineModel[]>(fetchUrl, { withCredentials: true });
                } catch (error) {
                    handleBackendHttpError(error);
                }
            },
            providesTags: (result) => [
                ...(result.map((machine) => ({ type: "AccountLabMachines", id: machine.id })) ??
                    []),
                { type: "AccountLabMachines", id: "LIST" },
            ],
        }),
        getLabMachineForAccount: builder.query<LabMachineModel, number>({
            queryFn: async (payload, api) => {
                const fetchUrl = buildResourcePathForCurrentUser(
                    api.getState() as RootState,
                    `/labs/settings/machines/${payload}`,
                );
                try {
                    return await axios.get(fetchUrl, { withCredentials: true });
                } catch (error) {
                    handleBackendHttpError(error);
                }
            },
        }),
        createLabMachineForAccount: builder.mutation<LabMachineModel, LabMachineModel>({
            queryFn: async (payload, api) => {
                const fetchUrl = buildResourcePathForCurrentUser(
                    api.getState() as RootState,
                    "/labs/settings/machines",
                );
                try {
                    return await axios.post(fetchUrl, payload, { withCredentials: true });
                } catch (error) {
                    handleBackendHttpError(error);
                }
            },
            invalidatesTags: [{ type: "AccountLabMachines", id: "LIST" }],
        }),
        updateLabMachineForAccount: builder.mutation<LabMachineModel, LabMachineModel>({
            queryFn: async (payload, api) => {
                const fetchUrl = buildResourcePathForCurrentUser(
                    api.getState() as RootState,
                    "/labs/settings/machines",
                );
                try {
                    return await axios.put(fetchUrl, payload, { withCredentials: true });
                } catch (error) {
                    handleBackendHttpError(error);
                }
            },
            invalidatesTags: (result) => [{ type: "AccountLabMachines", id: result.id }],
        }),
        deleteLabMachineForAccount: builder.mutation<void, LabMachineModel>({
            queryFn: async (payload, api) => {
                const fetchUrl = buildResourcePathForCurrentUser(
                    api.getState() as RootState,
                    `/labs/settings/machines/${payload.id}`,
                );
                try {
                    return await axios.delete(fetchUrl, { withCredentials: true });
                } catch (error) {
                    handleBackendHttpError(error);
                }
            },
            invalidatesTags: [{ type: "AccountLabMachines", id: "LIST" }],
        }),

        // =====================
        //  New BATCH Endpoints
        // =====================

        /**
         * POST /labs/settings/machines/batch
         * Sends an array of LabMachineModel. The server should upsert each item:
         * - If id is present, update the existing machine.
         * - If id is missing, create a new machine.
         * Returns an array of the upserted machines.
         */
        batchUpsertLabMachinesForAccount: builder.mutation<LabMachineModel[], LabMachineModel[]>({
            queryFn: async (payload, api) => {
                const fetchUrl = buildResourcePathForCurrentUser(
                    api.getState() as RootState,
                    "/labs/settings/machines/batch",
                );
                try {
                    const response = await axios.post<LabMachineModel[]>(fetchUrl, payload, {
                        withCredentials: true,
                    });
                    return { data: response.data };
                } catch (error) {
                    handleBackendHttpError(error);
                }
            },
            invalidatesTags: [{ type: "AccountLabMachines", id: "LIST" }],
        }),

        /**
         * DELETE /labs/settings/machines/batch
         * Sends an array of machine IDs (e.g. number[]). The server should delete
         * each corresponding machine if it belongs to the current account.
         * Returns no content (204).
         */
        batchDeleteLabMachinesForAccount: builder.mutation<void, number[]>({
            queryFn: async (payload, api) => {
                const fetchUrl = buildResourcePathForCurrentUser(
                    api.getState() as RootState,
                    "/labs/settings/machines/batch", // Your new batch delete endpoint
                );
                try {
                    // NOTE: axios.delete supports a `data` option for sending a request body
                    const response = await axios.delete<void>(fetchUrl, {
                        data: payload,
                        withCredentials: true,
                    });
                    return { data: response.data }; // likely `undefined` for 204
                } catch (error) {
                    handleBackendHttpError(error);
                }
            },
            invalidatesTags: [{ type: "AccountLabMachines", id: "LIST" }],
        }),
    }),
});

export const {
    useGetLabMachinesForAccountQuery,
    useGetLabMachineForAccountQuery,
    useCreateLabMachineForAccountMutation,
    useUpdateLabMachineForAccountMutation,
    useDeleteLabMachineForAccountMutation,
    useBatchUpsertLabMachinesForAccountMutation,
    useBatchDeleteLabMachinesForAccountMutation,
} = labsConfigurationApi;

export default labsConfigurationApi;
